'use client'
import { useSearchParams } from 'next/navigation'
import { type FC, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { type UserSubOrderV2 } from '@redteclab/api/clients/bully'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { type OpenableControls } from '../../../generic-hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { type PersonalAdviceProduct } from '../../../personal-advice'
import { TENANT } from '../../../tenant'

import { DirectContactFormComExperiment } from './DirectContactFormComExperiment'
import { DirectFormDialogContextProvider } from './DirectContactFormDialogContext'
import { DirectContactFormDefault } from './DirectContactFormForms/DirectContactFormDefault'

const OPEN_CONTACT_FORM_SEARCH_PARAM = 'openContactForm'

export const DirectContactFormDialog: FC<{
  disableSubject?: boolean
  formSubject?: string
  modalControls: OpenableControls
  onClose?: () => void
  onSuccess?: () => void
  onSuccessClose?: () => void
  prefilledSubOrder?: UserSubOrderV2
  product?: PersonalAdviceProduct
}> = ({
  disableSubject,
  formSubject,
  modalControls,
  onClose,
  onSuccess: handleSuccess,
  onSuccessClose,
  prefilledSubOrder,
  product,
}) => {
  const searchParams = useSearchParams()
  const [isInitialized, setIsInitialized] = useState(false)
  const [onSuccessSubmit, setOnSuccessSubmit] = useState(false)
  const [clearSubjectOnClose, setClearSubjectOnClose] = useState(false)

  const { tenant } = useGlobalConfigContext()

  const updatedContactFormTenants = [TENANT.COM, TENANT.AT]

  useEffect(() => {
    if (isInitialized) {
      return
    }
    if (searchParams.get(OPEN_CONTACT_FORM_SEARCH_PARAM) === 'true') {
      modalControls.handleOpen()
      setIsInitialized(true)
    }
  }, [searchParams, isInitialized, modalControls])

  const directContactForm = updatedContactFormTenants.includes(tenant) ? (
    <DirectContactFormComExperiment
      clearSubjectOnClose={clearSubjectOnClose}
      handleSuccess={handleSuccess}
      modalControls={modalControls}
      onSuccessClose={onSuccessClose}
      onSuccessSubmit={onSuccessSubmit}
      setClearSubjectOnClose={setClearSubjectOnClose}
      setOnSuccessSubmit={setOnSuccessSubmit}
    />
  ) : (
    <DirectContactFormDefault
      onFormSubmittedWithSuccess={() => {
        if (handleSuccess) {
          handleSuccess()
        }
      }}
    />
  )

  return (
    <DirectFormDialogContextProvider
      disableSubject={disableSubject}
      formSubject={formSubject}
      prefilledSubOrder={prefilledSubOrder}
      product={product}
    >
      <Dialog
        onClose={() => {
          setClearSubjectOnClose(!formSubject)
          setOnSuccessSubmit(false)
          modalControls.handleClose()
          if (onClose) {
            onClose()
            if (handleSuccess) {
              handleSuccess()
            }
          }
        }}
        open={modalControls.isOpen}
      >
        <DialogTitle
          onClose={() => {
            setClearSubjectOnClose(!formSubject)
            setOnSuccessSubmit(false)
            modalControls.handleClose()
            if (onClose) {
              onClose()
            }
          }}
        >
          {onSuccessSubmit ? (
            <span data-qa-id="direct-contact-form-submit-success-message">
              <FormattedMessage id="direct.contact.form.submit.success.message" />
            </span>
          ) : (
            <span data-qa-id="direct-contact-form-label-title">
              <FormattedMessage id="general.label.contactForm" />
            </span>
          )}
        </DialogTitle>
        <DialogContent className="flex h-full flex-col pt-1">
          {directContactForm}
        </DialogContent>
      </Dialog>
    </DirectFormDialogContextProvider>
  )
}
